<template>
  <div class="app-shell">
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark"><span></span></div>
        <div>
          <p class="eyebrow">OFFICE VISION</p>
          <h1>办公室摄像头监控</h1>
        </div>
      </div>
      <div class="header-actions">
        <div class="connection-pill" :class="mqttState">
          <i></i>{{ mqttStateText }}
        </div>
        <el-button class="settings-button" icon="el-icon-setting" @click="settingsVisible = true">连接设置</el-button>
      </div>
    </header>

    <main>
      <section class="overview">
        <div>
          <p class="section-kicker">LIVE CONTROL ROOM</p>
          <h2>{{ deviceStatus !== 'online' ? '等待摄像头设备上线' : (monitorActive ? '监控已开启，摄像头正在工作' : '设备在线，摄像头已休眠') }}</h2>
          <p class="overview-copy">设备 {{ config.deviceId }} · MQTT 视频通道</p>
        </div>
        <div class="clock">
          <strong>{{ currentTime }}</strong>
          <span>{{ currentDate }}</span>
        </div>
      </section>

      <section class="dashboard-grid">
        <article class="camera-panel panel">
          <div class="panel-heading">
            <div>
              <span class="panel-label">实时画面</span>
              <h3>主摄像头</h3>
            </div>
            <div class="live-badge" :class="{ active: streamOnline }"><i></i>{{ streamOnline ? 'LIVE' : 'OFFLINE' }}</div>
          </div>

          <div class="video-stage">
            <img v-if="mqttFrameUrl" :src="mqttFrameUrl" alt="办公室实时画面" @load="onStreamLoad" @error="onStreamError">
            <div v-if="!mqttFrameUrl" class="video-placeholder">
              <div class="lens"><div></div></div>
              <h4>{{ cameraStatus === 'error' ? '摄像头初始化失败' : (videoStatus === 'publish_failed' ? '视频分片发布失败' : (mqttState !== 'online' ? '正在连接 MQTT…' : (monitorActive ? '等待摄像头推送画面…' : '摄像头当前已关闭'))) }}</h4>
              <p>{{ monitorActive ? '视频直接通过 MQTT 传输' : '点击“开始监控”开启摄像头，60 秒后自动关闭' }}</p>
            </div>
            <div class="frame-corner top-left"></div><div class="frame-corner top-right"></div>
            <div class="frame-corner bottom-left"></div><div class="frame-corner bottom-right"></div>
          </div>

          <div class="camera-toolbar">
            <div class="camera-address"><span>MQTT STREAM</span><strong>office/camera/{{ config.deviceId }}/video/#</strong></div>
            <div class="toolbar-buttons">
              <el-button :type="monitorActive ? 'danger' : 'primary'" icon="el-icon-video-camera" :loading="monitorPending" :disabled="mqttState !== 'online'" @click="toggleMonitor">{{ monitorActive ? '停止监控' : '开始监控' }}</el-button>
              <el-button icon="el-icon-camera" :disabled="!mqttFrameUrl" @click="openCapture">保存当前帧</el-button>
            </div>
          </div>
        </article>

        <aside class="side-column">
          <device-status
            :telemetry="telemetry"
            :monitor-active="monitorActive"
            :remaining-seconds="remainingSeconds"
            :camera-status="cameraStatus"
            :video-status="videoStatus"
            :last-frame-bytes="lastFrameBytes"
            :frames-received="framesReceived"
          />

          <article class="panel control-panel">
            <div class="panel-heading compact"><div><span class="panel-label">远程操作</span><h3>设备控制</h3></div></div>
            <button class="control-action" :class="{ danger: monitorActive }" :disabled="mqttState !== 'online' || monitorPending" @click="toggleMonitor"><span class="action-icon"><i :class="monitorActive ? 'el-icon-video-pause' : 'el-icon-video-play'"></i></span><span><strong>{{ monitorPending ? '等待设备确认…' : (monitorActive ? '立即停止监控' : '开始监控 1 分钟') }}</strong><small>{{ monitorActive ? `剩余 ${remainingSeconds} 秒后自动关闭` : '按需开启摄像头，降低发热' }}</small></span><i class="el-icon-arrow-right arrow"></i></button>
            <button class="control-action danger" :disabled="mqttState !== 'online'" @click="restartDevice"><span class="action-icon"><i class="el-icon-switch-button"></i></span><span><strong>重启摄像头</strong><small>通过 MQTT 发送指令</small></span><i class="el-icon-arrow-right arrow"></i></button>
          </article>
        </aside>
      </section>
    </main>

    <connection-settings :visible.sync="settingsVisible" :config="config" @save="saveSettings" />
  </div>
</template>

<script>
import ConnectionSettings from './components/ConnectionSettings.vue'
import DeviceStatus from './components/DeviceStatus.vue'
import MqttService from './services/MqttService'
import VideoAssembler from './services/VideoAssembler'

const defaults = {
  deviceId: 'cam01',
  mqttUrl: 'wss://wxsky.com:58083',
  mqttUsername: '',
  mqttPassword: ''
}

export default {
  name: 'App',
  components: { ConnectionSettings, DeviceStatus },
  data () {
    const saved = JSON.parse(localStorage.getItem('officeCamConfig') || '{}')
    const config = { ...defaults, ...saved }
    return {
      config,
      settingsVisible: !config.mqttUsername || !config.mqttPassword,
      mqttService: null,
      mqttState: 'connecting',
      deviceStatus: 'offline',
      streamOnline: false,
      monitorActive: false,
      monitorPending: false,
      remainingSeconds: 0,
      cameraStatus: 'sleeping',
      videoStatus: 'idle',
      lastFrameBytes: 0,
      monitorAckTimer: null,
      mqttFrameUrl: '',
      videoAssembler: new VideoAssembler(),
      framesReceived: 0,
      frameTimeout: null,
      telemetry: { rssi: '', uptime: '', heap: '' },
      currentTime: '',
      currentDate: '',
      clockTimer: null
    }
  },
  computed: {
    mqttStateText () { return { online: 'MQTT 已连接', connecting: 'MQTT 连接中', offline: 'MQTT 已断开' }[this.mqttState] }
  },
  created () {
    this.updateClock()
    this.clockTimer = setInterval(this.updateClock, 1000)
    this.connectMqtt()
  },
  beforeDestroy () {
    clearInterval(this.clockTimer)
    clearTimeout(this.frameTimeout)
    clearTimeout(this.monitorAckTimer)
    if (this.mqttFrameUrl) URL.revokeObjectURL(this.mqttFrameUrl)
    if (this.mqttService) this.mqttService.disconnect()
  },
  methods: {
    updateClock () {
      const now = new Date()
      this.currentTime = now.toLocaleTimeString('zh-CN', { hour12: false })
      this.currentDate = now.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' })
    },
    connectMqtt () {
      if (this.mqttService) this.mqttService.disconnect()
      if (!this.config.mqttUsername || !this.config.mqttPassword) {
        this.mqttState = 'offline'
        return
      }
      this.mqttState = 'connecting'
      this.mqttService = new MqttService(this.config, {
        onConnect: () => { this.mqttState = 'online' },
        onReconnect: () => { this.mqttState = 'connecting' },
        onOffline: () => { this.mqttState = 'offline' },
        onError: () => { this.mqttState = 'offline' },
        onMessage: this.handleMqttMessage
      })
      this.mqttService.connect()
    },
    handleMqttMessage (topic, payload) {
      const suffix = topic.replace(`office/camera/${this.config.deviceId}`, '')
      if (suffix.startsWith('/video/')) {
        this.handleVideoChunk(suffix, payload)
        return
      }
      const value = payload.toString()
      if (suffix === '/status') this.deviceStatus = value
      if (suffix === '/monitor') {
        this.monitorPending = false
        clearTimeout(this.monitorAckTimer)
        this.monitorActive = value === 'on'
        if (!this.monitorActive) {
          this.remainingSeconds = 0
          this.streamOnline = false
          clearTimeout(this.frameTimeout)
        }
      }
      if (suffix === '/monitor/remaining_s') this.remainingSeconds = Number(value) || 0
      if (suffix === '/camera/status') {
        this.cameraStatus = value
        if (value === 'error') this.$message.error('设备报告：摄像头初始化失败')
      }
      if (suffix === '/video/status') {
        this.videoStatus = value
        if (value === 'publish_failed') this.$message.error('设备报告：MQTT 视频分片发布失败')
      }
      if (suffix === '/video/last_frame_bytes') this.lastFrameBytes = Number(value) || 0
      if (suffix === '/event' && value === 'camera_capture_failed') this.$message.error('设备报告：摄像头抓帧失败')
      if (suffix === '/telemetry/rssi') this.telemetry.rssi = value
      if (suffix === '/telemetry/uptime_s') this.telemetry.uptime = value
      if (suffix === '/telemetry/free_heap') this.telemetry.heap = value
    },
    handleVideoChunk (suffix, payload) {
      const blob = this.videoAssembler.push(suffix, payload)
      if (!blob) return
      const nextUrl = URL.createObjectURL(blob)
      const previousUrl = this.mqttFrameUrl
      this.mqttFrameUrl = nextUrl
      this.streamOnline = true
      this.framesReceived += 1
      if (previousUrl) this.$nextTick(() => URL.revokeObjectURL(previousUrl))
      clearTimeout(this.frameTimeout)
      this.frameTimeout = setTimeout(() => { this.streamOnline = false }, 2500)
    },
    saveSettings (config) {
      this.config = { ...config }
      localStorage.setItem('officeCamConfig', JSON.stringify(this.config))
      this.settingsVisible = false
      this.resetVideo()
      this.connectMqtt()
      this.$message.success('配置已保存')
    },
    toggleMonitor () {
      if (!this.mqttService || !this.mqttService.connected) {
        this.$message.warning('MQTT 尚未连接')
        return
      }
      const enabled = !this.monitorActive
      this.monitorPending = true
      clearTimeout(this.monitorAckTimer)
      this.mqttService.publishCommand('monitor', enabled ? '1' : '0', error => {
        if (error) {
          this.monitorPending = false
          this.$message.error(`监控指令发送失败：${error.message}`)
          return
        }
        if (!this.monitorPending) return
        this.monitorAckTimer = setTimeout(() => {
          this.monitorPending = false
          this.$message.error('Broker 已接收指令，但设备 12 秒内没有确认')
        }, 12000)
      })
    },
    resetVideo () {
      clearTimeout(this.frameTimeout)
      if (this.mqttFrameUrl) URL.revokeObjectURL(this.mqttFrameUrl)
      this.mqttFrameUrl = ''
      this.videoAssembler.reset()
      this.streamOnline = false
    },
    onStreamLoad () { this.streamOnline = true },
    onStreamError () { this.streamOnline = false },
    openCapture () {
      const link = document.createElement('a')
      link.href = this.mqttFrameUrl
      link.download = `${this.config.deviceId}-${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`
      link.click()
    },
    restartDevice () {
      this.$confirm('确定要重启摄像头设备吗？画面会短暂中断。', '重启确认', { type: 'warning' }).then(() => {
        this.mqttService.publishCommand('restart', '1', error => {
          if (error) this.$message.error(`重启指令发送失败：${error.message}`)
          else this.$message.success('Broker 已接收重启指令')
        })
      }).catch(() => {})
    }
  }
}
</script>
